import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { ReactElement, Suspense } from "react";
import { lazyWithRetry } from "./lazyWithRetry";
import RouteErrorBoundary from "./RouteErrorBoundary";
import Loader from "components/shared/loader";
import { accessEnum } from "components/access-control/accessEnum";
import NoRouteMatch from "./NoRouteMatch";
import PrivateRoute from "./PrivateRoute";
import { isContentDashboardEnabled } from "utils/featureFlags";

const TariffAnalyser = lazyWithRetry(() => import("pages/tariff-analyser"));
const TCDashboard = lazyWithRetry(() => import("../../components/layout/tradecompliance-dashboard/tradecompliance-dashboard"));
const AllSkus = lazyWithRetry(() => import("../../pages/all-skus/allSkus"));
const BulkValidation = lazyWithRetry(() => import("../../pages/bulk-validation/bulk-validation"));
const ClassificationRequests = lazyWithRetry(() => import("../../pages/all-requests/ClassificationRequests"));
const ExternalRfiStepperPage = lazyWithRetry(() => import("../../pages/rfi/external/ExternalRfiStepperPage"));
const ExternalRfiResponsesPage = lazyWithRetry(() => import("../../pages/rfi/external/responses/ExternalRfiResponsesPage"));
const OpenedRequests = lazyWithRetry(() => import("../../pages/all-requests/OpenedRequests"));
const Controls = lazyWithRetry(() => import("../../pages/controls-module/controls"));
const DocumentDetermination = lazyWithRetry(() => import("../../pages/document-determination/documentDetermination"));
const ECCNLicenseDetermination = lazyWithRetry(() => import("../../pages/eccn-license-determination/licence-determination-engine"));
const Embargoes = lazyWithRetry(() => import("../../pages/embargoes-module/embargoes"));
const LandedCostCalculator = lazyWithRetry(() => import("../../pages/landed-cost-calculator-new"));
const TcMiddleLayer = lazyWithRetry(() => import("../../pages/middle-layer-handler"));
const Eccn = lazyWithRetry(() => import("../../pages/tc-classify/eccn-classification"));
const TCClassify = lazyWithRetry(() => import("../../pages/tc-classify/tc-classify"));
const FreeTrialLandingPage = lazyWithRetry(() => import("../layouts/tc-layout/free-trial-landing-page"));
const UploadLogs = lazyWithRetry(() => import("pages/upload-logs/uploadLogs"));
const SkuDetails = lazyWithRetry(() => import("pages/all-skus/skuDetails"));
const EscalationDetails = lazyWithRetry(() => import("pages/escalations/escalationDetails"));
const Watchlist = lazyWithRetry(() => import("pages/watchlist"));
const WatchlistLogs = lazyWithRetry(() => import("pages/watchlist/watchlistLogs"));
const Classifier = lazyWithRetry(() => import("pages/hs-classifier/classifier"));
const EccnClassifier = lazyWithRetry(() => import("pages/eccn-classifier/eccnClassifier"));
const AllRequests = lazyWithRetry(() => import("../../pages/all-requests/AllRequests"));
const MyTasks = lazyWithRetry(() => import("pages/my-tasks"));
const Dashboard = lazyWithRetry(() => import("pages/requests-dashboard/dashboard"));
const BomMaster = lazyWithRetry(() => import("pages/bom-master/bomMaster"));
const PartnerMaster = lazyWithRetry(() => import("pages/partner-master/partnerMaster"));
const PartnerDetail = lazyWithRetry(() => import("pages/partner-master/partnerDetail"));
const SupplierSolicitation = lazyWithRetry(() => import("pages/supplier-solicitation"));
const TradeLanes = lazyWithRetry(() => import("pages/trade-lanes/tradeLanes"));
const LazyClassifierWidget = lazyWithRetry(() => import("pages/hs-classifier-widget"));
const RiskThresholds = lazyWithRetry(() => import("pages/risk-thresholds"));
const WorkspacePage = lazyWithRetry(() => import("pages/rfi/workspace/WorkspacePage"));
const WorkspaceRequestsPage = lazyWithRetry(() => import("pages/rfi/workspace/WorkspaceRequestsPage"));
const ExternalRfiListPage = lazyWithRetry(() => import("pages/rfi/workspace/ExternalRfiListPage"));
const WorkspaceLoginPage = lazyWithRetry(() => import("pages/rfi/workspace/WorkspaceLoginPage"));
const WorkspaceSessionGuard = lazyWithRetry(() => import("pages/rfi/workspace/WorkspaceSessionGuard"));
const NexaWorkspace = lazyWithRetry(() => import("pages/nexa-ai/NexaWorkspace"));
const ContentDashboard = lazyWithRetry(() => import("../../components/content-dashboard/ContentDashboard"));

interface RouteConfig {
  path: string;
  element: ReactElement;
  access?: string;
  isInternal?: boolean;
}

const routesConfig: RouteConfig[] = [
  { path: "/", element: <TcMiddleLayer /> },
  { path: "/tc-dashboard", element: <TCDashboard /> },
  { path: "/landedcostcalculator", element: <LandedCostCalculator /> },
  { path: "/classify", element: <TCClassify />, access: accessEnum.TARIFF_DUTIES_MODULE_ACCESS },
  { path: "/eccn-classification", element: <Eccn />, access: accessEnum.ECCN_SEARCH_MODULE_ACCESS },
  { path: "/validate", element: <BulkValidation /> },
  { path: "/controls", element: <Controls />, access: accessEnum.CONTROLS_SEARCH_MODULE_ACCESS },
  { path: "/embargoes", element: <Embargoes />, access: accessEnum.EMBARGOES_MODULE_ACCESS },
  { path: "/document-determination", element: <DocumentDetermination />, access: accessEnum.DOCUMENT_DETERMINATION_MODULE_ACCESS },
  { path: "/eccn-license-determination", element: <ECCNLicenseDetermination />, access: accessEnum.ECCN_LICENSES_DETERMINATION_MODULE_ACCESS },
  { path: "/all-requests", element: <AllRequests />, access: accessEnum.CLASSIFICATION_REQUEST_MODULE },
  { path: "/all-requests/:transaction_id", element: <OpenedRequests /> },
  { path: "/all-requests/classification-requests/:transaction_id", element: <ClassificationRequests />, access: accessEnum.CLASSIFICATION_REQUEST_MODULE },
  {
    path: "/all-requests/classification-requests/:transaction_id/external-rfi/:id/:step",
    element: <ExternalRfiStepperPage />,
    access: accessEnum.CLASSIFICATION_REQUEST_MODULE,
  },
  {
    path: "/all-requests/classification-requests/:transaction_id/external-rfi/:id/responses",
    element: <ExternalRfiResponsesPage />,
    access: accessEnum.CLASSIFICATION_REQUEST_MODULE,
  },
  { path: "/all-skus/:sku_code", element: <SkuDetails />, access: accessEnum.VIEW_SKU_DETAILS },
  { path: "/compare-tariff", element: <TariffAnalyser /> },
  { path: "/all-skus", element: <AllSkus />, access: accessEnum.ALL_SKUS_MODULE_ACCESS },
  { path: "/upload-logs", element: <UploadLogs />, access: accessEnum.UPLOAD_LOGS_MODULE },
  { path: "/nexa-ai", element: <NexaWorkspace />, access: accessEnum.NEXA_AI_MODULE_ACCESS },
  { path: "/dashboard", element: <Dashboard />, access: accessEnum.REQUESTS_DASHBOARD_MODULE_ACCESS },
  {
    path: "/watchlist",
    element: <Watchlist />,
    access: accessEnum.WATCHLIST_MODULE_ACCESS,
  },
  {
    path: "/watchlist/:tab/:watchlist_id",
    element: <WatchlistLogs />,
    access: accessEnum.WATCHLIST_MODULE_ACCESS,
  },
  {
    path: "/my-tasks",
    element: <MyTasks />,
    access: accessEnum.MY_TASKS_MODULE_ACCESS,
  },
  {
    path: "/my-tasks/escalations/:escalation_id",
    element: <EscalationDetails />,
    access: accessEnum.MY_TASKS_MODULE_ACCESS,
  },
  {
    path: "/my-tasks/:transaction_id",
    element: <OpenedRequests />,
    access: accessEnum.MY_TASKS_MODULE_ACCESS,
  },
  {
    path: "/my-tasks/classification-requests/:transaction_id",
    element: <ClassificationRequests />,
    access: accessEnum.MY_TASKS_MODULE_ACCESS,
  },
  {
    path: "/my-tasks/classification-requests/:transaction_id/external-rfi/:id/:step",
    element: <ExternalRfiStepperPage />,
    access: accessEnum.MY_TASKS_MODULE_ACCESS,
  },
  {
    path: "/my-tasks/classification-requests/:transaction_id/external-rfi/:id/responses",
    element: <ExternalRfiResponsesPage />,
    access: accessEnum.MY_TASKS_MODULE_ACCESS,
  },
  {
    path: "/my-tasks/rfi-requests/:thread_id",
    element: <ClassificationRequests />,
    access: accessEnum.MY_TASKS_MODULE_ACCESS,
  },
  {
    path: "/trade-lanes",
    element: <TradeLanes />,
    access: accessEnum.TRADE_LANE_MASTER_MODULE_ACCESS,
  },
  {
    path: "/bom-master",
    element: <BomMaster />,
    access: accessEnum.BOM_MASTER_MODULE_ACCESS,
  },
  {
    path: "/partner-master",
    element: <PartnerMaster />,
    access: accessEnum.PARTNER_MASTER_MODULE_ACCESS,
  },
  {
    path: "/partner-master/:partner_id",
    element: <PartnerDetail />,
    access: accessEnum.PARTNER_MASTER_MODULE_ACCESS,
  },
  {
    path: "/supplier-solicitation",
    element: <SupplierSolicitation />,
    access: accessEnum.SUPPLIER_SOLICITATION_MODULE_ACCESS,
  },
  {
    path: "/hs-classifier",
    element: <Classifier />,
    isInternal: true,
  },
  {
    path: "/eccn-classifier",
    element: <EccnClassifier />,
    isInternal: true,
  },
  {
    path: "/escalations/:escalation_id",
    element: <EscalationDetails />,
    access: accessEnum.VIEW_ESCALATIONS,
  },
  {
    path: "/lcc-embed",
    element: <LandedCostCalculator isEmbed={true} />,
  },
  {
    path: "/classifier-embed",
    element: <LazyClassifierWidget isEmbed={true} />,
  },
  {
    path: "/configuration/risk-thresholds",
    element: <RiskThresholds />,
    access: accessEnum.RISK_CONFIGURATION_ACCESS,
  },
  ...(isContentDashboardEnabled ? [{ path: "/content", element: <ContentDashboard />, isInternal: true }] : []),
  { path: "/free-trial", element: <FreeTrialLandingPage /> },
  // Public - an external supplier landing here from their RFI email has no in-app login; the page
  // itself gates on a verified OTP session (see WorkspacePage).
  {
    path: "/workspace/list",
    element: (
      <WorkspaceSessionGuard>
        <ExternalRfiListPage />
      </WorkspaceSessionGuard>
    ),
  },
  {
    path: "/workspace/requests/:externalRfiId",
    element: (
      <WorkspaceSessionGuard>
        <WorkspaceRequestsPage />
      </WorkspaceSessionGuard>
    ),
  },
  { path: "/workspace/login", element: <WorkspaceLoginPage /> },
  { path: "/workspace/:workspaceToken", element: <WorkspacePage /> },

  { path: "*", element: <NoRouteMatch /> },
];

export default function RoutesPath() {
  return (
    <Router>
      <RouteErrorBoundary>
        <Suspense fallback={<Loader />}>
          <Routes>
            {routesConfig.map(({ path, element, access, isInternal }, idx) => (
              <Route
                key={idx}
                path={path}
                element={
                  access || isInternal ? (
                    <PrivateRoute permissionKey={access} isInternal={!!isInternal}>
                      {element}
                    </PrivateRoute>
                  ) : (
                    element
                  )
                }
              />
            ))}
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </Router>
  );
}
